import { useState } from "react";

import { Utils } from '../Utils';

import './DatePicker.scss';

const WEEKDAYS = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];

const startOfDay = function (date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const monthName = function (monthIdx) {
    return new Date(2000, monthIdx, 1).toLocaleDateString("he-IL", { month: "long" });
}

const DatePicker = function ({ onDateSelected, onCancel, allowFuture = true, initialDate, title = "יש לבחור תאריך", yearsBack = 10 }) {
    const today = startOfDay(new Date());

    const [stateSelected, setStateSelected] = useState(initialDate ? startOfDay(new Date(initialDate)) : null);
    const [stateView, setStateView] = useState(() => {
        let d = initialDate ? new Date(initialDate) : new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [statePickerOpen, setStatePickerOpen] = useState(false);

    const year = stateView.getFullYear();
    const month = stateView.getMonth();

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();

    const isBlocked = (date) => (!allowFuture && date.getTime() > today.getTime());

    const isSameDay = (a, b) => (a && b && a.getTime() === b.getTime());

    const monthLabel = stateView.toLocaleDateString("he-IL", { month: "long", year: "numeric" });

    const selectedLabel = stateSelected
        ? stateSelected.toLocaleDateString("he-IL", { day: "numeric", month: "short", year: "numeric" })
        : "";

    // the last month the user is allowed to browse to
    const nextBlocked = !allowFuture && (year > today.getFullYear() || (year === today.getFullYear() && month >= today.getMonth()));

    const handlePrevMonth = () => {
        setStateView(new Date(year, month - 1, 1));
    };

    const handleNextMonth = () => {
        if (nextBlocked) return;
        setStateView(new Date(year, month + 1, 1));
    };

    const handleTogglePicker = () => {
        setStatePickerOpen(!statePickerOpen);
    };

    const handleSelectMonth = (monthIdx) => {
        if (isBlocked(new Date(year, monthIdx, 1))) return;
        setStateView(new Date(year, monthIdx, 1));
    };

    const handleSelectYear = (yearNum) => {
        if (isBlocked(new Date(yearNum, month, 1))) {
            // jumping back to a year where the viewed month is in the future - clamp to today's month
            setStateView(new Date(yearNum, today.getMonth(), 1));
            return;
        }
        setStateView(new Date(yearNum, month, 1));
    };

    const handleSelectDay = (day) => {
        let newDate = new Date(year, month, day);
        if (isBlocked(newDate)) return;

        setStateSelected(newDate);
        setStatePickerOpen(false);
    };

    const handleSubmit = (e) => {
        if (!stateSelected) return;

        onDateSelected?.(Utils.dateObjToString(stateSelected), e);
    };

    const handleCancel = (e) => {
        onCancel?.(e);
    };

    // newest first - always wide enough to contain the year currently being viewed
    let yearsTo = Math.max(year, today.getFullYear() + (allowFuture ? 5 : 0));
    let yearsFrom = Math.min(year, today.getFullYear() - yearsBack);

    let years = [];
    for (let y = yearsTo; y >= yearsFrom; y--) {
        years.push(y);
    }

    let days = [];
    for (let i = 0; i < firstDay; i++) {
        days.push(<div className="dp-day blank" key={"blank" + i}></div>);
    }
    for (let day = 1; day <= daysInMonth; day++) {
        let date = new Date(year, month, day);
        let classes = "dp-day"
            + (isSameDay(date, stateSelected) ? " selected" : "")
            + (isSameDay(date, today) ? " today" : "")
            + (isBlocked(date) ? " disabled" : "");

        days.push(
            <div className={classes} key={day} onClick={() => handleSelectDay(day)}>{day}</div>
        );
    }

    return (
        <div className="datepicker">
            <div className="dp-header">
                <div className="dp-title">{title}</div>
                <div className="dp-selected">{selectedLabel}</div>
            </div>

            <div className="dp-body">
                <div className="dp-nav">
                    <div className="dp-arrows">
                        <button className={"dp-arrow" + (nextBlocked ? " disabled" : "")} onClick={handleNextMonth}>‹</button>
                        <button className="dp-arrow" onClick={handlePrevMonth}>›</button>
                    </div>
                    <button className={"dp-label" + (statePickerOpen ? " on" : "")} onClick={handleTogglePicker}>
                        <span>{monthLabel}</span>
                        <img src="img/chevron-down.svg" alt="" />
                    </button>
                </div>

                {statePickerOpen &&
                    <div className="dp-picker">
                        <div className="dp-months">
                            {Array.from({ length: 12 }).map((_, i) => (
                                <button
                                    key={i}
                                    className={"dp-month" + (i === month ? " selected" : "") + (isBlocked(new Date(year, i, 1)) ? " disabled" : "")}
                                    onClick={() => handleSelectMonth(i)}
                                >{monthName(i)}</button>
                            ))}
                        </div>
                        <div className="dp-years">
                            {years.map(y => (
                                <button
                                    key={y}
                                    className={"dp-year" + (y === year ? " selected" : "")}
                                    onClick={() => handleSelectYear(y)}
                                >{y}</button>
                            ))}
                        </div>
                    </div>
                }

                <div className="dp-weekdays">
                    {WEEKDAYS.map(d => <span key={d}>{d}</span>)}
                </div>

                <div className="dp-days">{days}</div>
            </div>

            <div className="blueLine"></div>

            <div className="dialog-buttons">
                <button className={"orange" + (!stateSelected ? " disabled" : "")} onClick={handleSubmit}>אישור</button>
                <button onClick={handleCancel}>ביטול</button>
            </div>
        </div>
    );
}

export { DatePicker }
